// src/components/classroom/ClassroomDeviceSetup.jsx
import React, { useEffect, useRef } from "react";
import {
  FaMicrophone,
  FaMicrophoneSlash,
  FaVideo,
  FaVideoSlash,
  FaShieldAlt,
  FaInfoCircle,
} from "react-icons/fa";

export default function ClassroomDeviceSetup({
  camActive,
  micActive,
  onToggleCam,
  onToggleMic,
  previewStream,
  isDarkMode = true,
  allowStudentAudio = true,
  allowStudentVideo = false,
  isHost = false,
}) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && previewStream) {
      videoRef.current.srcObject = previewStream;
    }
  }, [previewStream]);

  const canUseVideo = isHost || allowStudentVideo;
  const canUseAudio = isHost || allowStudentAudio;

  return (
    <div className="space-y-4">
      {/* Video Preview Box */}
      <div
        className={`relative overflow-hidden rounded-3xl border shadow-xl aspect-video flex items-center justify-center ${
          isDarkMode
            ? "border-slate-800 bg-slate-900"
            : "border-slate-200 bg-slate-900 text-white"
        }`}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full object-cover ${!camActive || !canUseVideo ? "hidden" : ""}`}
        />

        {(!camActive || !canUseVideo) && (
          <div className="text-center p-6 text-slate-400">
            <FaVideoSlash className="mx-auto text-4xl mb-2 opacity-50" />
            <p className="text-sm font-semibold">
              {!canUseVideo
                ? "Camera Disabled by Host"
                : "Camera is Off"}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {!canUseVideo
                ? "This lecture is configured as audio-only for students."
                : "You will enter with your camera turned off."}
            </p>
          </div>
        )}

        {/* Floating Device Controls */}
        <div className="absolute bottom-4 inset-x-0 flex items-center justify-center gap-3">
          <button
            type="button"
            disabled={!canUseAudio}
            onClick={onToggleMic}
            className={`h-11 w-11 rounded-full flex items-center justify-center shadow-lg transition ${
              !canUseAudio
                ? "bg-slate-800 text-slate-600 cursor-not-allowed"
                : micActive
                ? "bg-slate-800/90 hover:bg-slate-700 text-white"
                : "bg-rose-600 hover:bg-rose-700 text-white"
            }`}
            title={
              !canUseAudio
                ? "Microphone disabled by instructor"
                : micActive
                ? "Mute Microphone"
                : "Unmute Microphone"
            }
          >
            {micActive && canUseAudio ? <FaMicrophone /> : <FaMicrophoneSlash />}
          </button>

          <button
            type="button"
            disabled={!canUseVideo}
            onClick={onToggleCam}
            className={`h-11 w-11 rounded-full flex items-center justify-center shadow-lg transition ${
              !canUseVideo
                ? "bg-slate-800 text-slate-600 cursor-not-allowed"
                : camActive
                ? "bg-slate-800/90 hover:bg-slate-700 text-white"
                : "bg-rose-600 hover:bg-rose-700 text-white"
            }`}
            title={
              !canUseVideo
                ? "Camera disabled by instructor"
                : camActive
                ? "Turn Off Camera"
                : "Turn On Camera"
            }
          >
            {camActive && canUseVideo ? <FaVideo /> : <FaVideoSlash />}
          </button>
        </div>
      </div>

      {/* Permissions Breakdown Notice */}
      <div
        className={`rounded-2xl border p-4 text-xs ${
          isDarkMode
            ? "border-slate-800 bg-slate-900/60 text-slate-400"
            : "border-slate-200 bg-white text-slate-600"
        }`}
      >
        <div className="flex items-center gap-2 font-bold text-sky-500 mb-2">
          <FaShieldAlt /> Session Media Policy:
        </div>

        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <div
            className={`rounded-xl border p-2.5 flex items-center gap-2 ${
              canUseAudio
                ? "border-emerald-500/20 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400"
                : "border-slate-700 bg-slate-800/30 text-slate-400"
            }`}
          >
            {canUseAudio ? <FaMicrophone className="text-xs" /> : <FaMicrophoneSlash className="text-xs" />}
            <div>
              <p className="font-bold">Student Audio</p>
              <p className="text-[10px] opacity-80">{canUseAudio ? "Microphone Allowed" : "Muted by Host"}</p>
            </div>
          </div>

          <div
            className={`rounded-xl border p-2.5 flex items-center gap-2 ${
              canUseVideo
                ? "border-emerald-500/20 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400"
                : "border-slate-700 bg-slate-800/30 text-slate-400"
            }`}
          >
            {canUseVideo ? <FaVideo className="text-xs" /> : <FaVideoSlash className="text-xs" />}
            <div>
              <p className="font-bold">Student Video</p>
              <p className="text-[10px] opacity-80">{canUseVideo ? "Camera Allowed" : "Audio-Only Mode"}</p>
            </div>
          </div>
        </div>

        {!isHost && !allowStudentVideo && (
          <p className="mt-2 text-[10px] text-slate-500 flex items-center gap-1">
            <FaInfoCircle /> Student cameras are disabled by default to optimize bandwidth and focus.
          </p>
        )}
      </div>
    </div>
  );
}
